import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Flame, Leaf, Wind, Waves, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const COPY = {
  overline: { en: "Wellness · Hammam · Argan", fr: "Bien-être · Hammam · Argan", es: "Bienestar · Hammam · Argán" },
  experiences: { en: "Experiences", fr: "Expériences", es: "Experiencias" },
  offerings: { en: "Signature treatments", fr: "Soins signature", es: "Tratamientos insignia" },
  spaces: { en: "Spaces", fr: "Espaces", es: "Espacios" },
  cta_default: { en: "Reserve a spa ritual", fr: "Réserver un rituel spa", es: "Reservar un ritual de spa" },
  view_menu: { en: "View the full spa menu", fr: "Voir la carte complète", es: "Ver la carta completa" },
};

const SPACE_ICONS = [Flame, Waves, Leaf, Wind, Sparkles];

const SpaWellnessFeature = ({ feature, spaMenu = [], hotelSlug, hotelName }) => {
  const { lang } = useLanguage();
  if (!feature) return null;

  const offerings = (feature.offerings && feature.offerings.length ? feature.offerings : spaMenu).slice(0, 4);
  const ctaLabel = pick(feature.cta_label, lang) || pick(COPY.cta_default, lang);
  const ctaHref = feature.cta_to || `/contact?topic=${encodeURIComponent(`Spa enquiry — ${hotelName}`)}`;

  return (
    <section
      id="spa-wellness"
      className="bg-[#14100E] text-[#FDFBF7] relative overflow-hidden"
      data-testid="section-spa-wellness"
      // Per-hotel scope so the wellness hero photo maps to a
      // deterministic slot in /admin/images. Slot id resolves to
      // `cms.hotel.<hotelSlug>.spa-wellness.hero`.
      data-cms-scope={hotelSlug ? `hotel.${hotelSlug}.spa-wellness` : "hotel.spa-wellness"}
    >
      {/* ambient gold wash */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]" aria-hidden="true"
        style={{ background: "radial-gradient(ellipse at top right, #D4A373 0%, transparent 55%)" }} />
      {/* Berber cross-lattice pattern overlay — cultural texture */}
      <div className="absolute inset-0 berber-bg-cross opacity-30 pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
        {/* Hero: image + editorial intro */}
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-stretch">
          <div className="lg:col-span-7 relative overflow-hidden min-h-[340px] md:min-h-[460px]">
            <img
              src={feature.image}
              alt={`${hotelName} — spa & wellness`}
              data-cms-role="hero"
              className="ken-burns absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <span className="film-grain" aria-hidden="true" />
            <div className="absolute bottom-5 left-5 md:bottom-7 md:left-7 text-[10px] tracking-[0.3em] uppercase text-[#FDFBF7]/85 bg-[#1A1513]/55 backdrop-blur-sm px-3 py-1.5 border border-[#D4A373]/40">
              {pick(feature.signature_label, lang) || pick({ en: "Signature wellness", fr: "Bien-être signature", es: "Bienestar insignia" }, lang)}
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-center">
            <p className="overline !text-[#D4A373] flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
              {pick(COPY.overline, lang)}
            </p>
            <h2 className="font-serif-x mt-5 text-4xl md:text-5xl lg:text-[56px] leading-[1.02] tracking-tight">
              {pick(feature.title, lang) || pick({ en: "Spa & Wellness", fr: "Spa & Bien-être", es: "Spa y Bienestar" }, lang)}
              {feature.title_accent && (
                <>
                  <br />
                  <em className="not-italic text-[#D4A373]">{pick(feature.title_accent, lang)}</em>
                </>
              )}
            </h2>
            <p className="mt-7 text-base md:text-lg leading-relaxed text-[#FDFBF7]/82">
              {pick(feature.intro, lang)}
            </p>
            {feature.stats && feature.stats.length > 0 && (
              <ul className="mt-7 grid grid-cols-3 gap-4 border-t border-[#FDFBF7]/15 pt-6" data-testid="spa-stats">
                {feature.stats.map((s, i) => (
                  <li key={`spa-stat-${pick(s.label, "en") || i}`} className="text-[#FDFBF7]/85">
                    <span className="block font-serif-x text-2xl md:text-3xl text-[#D4A373]">{pick(s.value, lang)}</span>
                    <span className="text-[10px] tracking-[0.25em] uppercase text-[#FDFBF7]/55 mt-1 block">
                      {pick(s.label, lang)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Three columns: Experiences / Offerings / Spaces */}
        <div className="mt-20 md:mt-24 grid md:grid-cols-3 gap-px bg-[#FDFBF7]/10 border border-[#FDFBF7]/10">
          {/* Experiences */}
          <div className="bg-[#14100E] p-8 md:p-9" data-testid="spa-experiences">
            <p className="overline !text-[#D4A373]">{pick(COPY.experiences, lang)}</p>
            <h3 className="font-serif-x mt-3 text-2xl md:text-3xl tracking-tight leading-tight">
              {pick(
                feature.experiences_title || { en: "Hammam & rituals", fr: "Hammam & rituels", es: "Hammam y rituales" },
                lang
              )}
            </h3>
            <ul className="mt-6 space-y-5">
              {(feature.experiences || []).map((ex, i) => (
                <li
                  key={`spa-exp-${pick(ex.title, "en") || i}`}
                  className="flex gap-4"
                  data-testid={`spa-experience-${i}`}
                >
                  <span className="mt-1 font-serif-x text-xl text-[#D4A373] w-8 shrink-0 tabular-nums">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h4 className="font-serif-x text-lg text-[#FDFBF7] leading-snug">{pick(ex.title, lang)}</h4>
                    <p className="mt-1 text-sm text-[#FDFBF7]/70 leading-relaxed">{pick(ex.desc, lang)}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Offerings */}
          <div className="bg-[#14100E] p-8 md:p-9" data-testid="spa-offerings">
            <p className="overline !text-[#D4A373]">{pick(COPY.offerings, lang)}</p>
            <h3 className="font-serif-x mt-3 text-2xl md:text-3xl tracking-tight leading-tight">
              {pick(
                feature.offerings_title || { en: "Massages & wraps", fr: "Massages & enveloppements", es: "Masajes y envolturas" },
                lang
              )}
            </h3>
            <ul className="mt-6 divide-y divide-[#FDFBF7]/10">
              {offerings.map((o, i) => {
                const ritualName = pick(o.title, "en") || pick(o.title, lang);
                const ritualHref = `${ctaHref}${ctaHref.includes("?") ? "&" : "?"}ritual=${encodeURIComponent(ritualName)}`;
                return (
                  <li
                    key={`spa-offer-${ritualName || i}`}
                    data-testid={`spa-offering-${i}`}
                  >
                    <Link
                      to={ritualHref}
                      className="group/offer flex items-start justify-between gap-4 py-4 first:pt-0 hover:bg-[#FDFBF7]/[0.03] -mx-2 px-2 transition-colors"
                      data-testid={`spa-offering-cta-${i}`}
                    >
                      <div className="flex-1 min-w-0">
                        <h4 className="font-serif-x text-base md:text-lg text-[#FDFBF7] leading-snug group-hover/offer:text-[#D4A373] transition-colors">
                          {pick(o.title, lang)}
                        </h4>
                        {o.duration && (
                          <span className="block mt-1 text-[10px] tracking-[0.25em] uppercase text-[#FDFBF7]/55">
                            {o.duration}
                          </span>
                        )}
                        <span className="mt-2 inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase text-[#D4A373]/70 group-hover/offer:text-[#D4A373] opacity-0 group-hover/offer:opacity-100 -translate-y-0.5 group-hover/offer:translate-y-0 transition-all duration-300">
                          {pick({ en: "Book this ritual", fr: "Réserver ce rituel", es: "Reservar este ritual" }, lang)}
                          <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                        </span>
                      </div>
                      {o.price && (
                        <span className="font-serif-x text-base md:text-lg text-[#D4A373] whitespace-nowrap shrink-0">
                          {o.price}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
            <Link
              to="#spa"
              className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#D4A373] border-b border-[#D4A373]/40 hover:border-[#D4A373] pb-1 transition-colors"
              data-testid="spa-view-menu"
            >
              {pick(COPY.view_menu, lang)} <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
            </Link>
          </div>

          {/* Spaces */}
          <div className="bg-[#14100E] p-8 md:p-9" data-testid="spa-spaces">
            <p className="overline !text-[#D4A373]">{pick(COPY.spaces, lang)}</p>
            <h3 className="font-serif-x mt-3 text-2xl md:text-3xl tracking-tight leading-tight">
              {pick(
                feature.spaces_title || { en: "Relaxation areas", fr: "Espaces de détente", es: "Zonas de relajación" },
                lang
              )}
            </h3>
            <ul className="mt-6 space-y-4">
              {(feature.spaces || []).map((sp, i) => {
                const Icon = SPACE_ICONS[i % SPACE_ICONS.length];
                return (
                  <li
                    key={`spa-space-${pick(sp.name, "en") || i}`}
                    className="flex gap-4 items-start"
                    data-testid={`spa-space-${i}`}
                  >
                    <span className="mt-0.5 inline-flex items-center justify-center w-9 h-9 border border-[#D4A373]/40 text-[#D4A373] shrink-0">
                      <Icon className="w-4 h-4" strokeWidth={1.5} />
                    </span>
                    <div>
                      <h4 className="font-serif-x text-lg text-[#FDFBF7] leading-snug">{pick(sp.name, lang)}</h4>
                      <p className="mt-1 text-sm text-[#FDFBF7]/70 leading-relaxed">{pick(sp.detail, lang)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-14 md:mt-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-10 border-t border-[#FDFBF7]/15">
          <p className="text-sm md:text-base text-[#FDFBF7]/70 max-w-2xl leading-relaxed">
            {pick(
              feature.cta_intro || {
                en: "Our therapists tailor each ritual to your stay — speak to the spa concierge for a bespoke programme or to reserve before arrival.",
                fr: "Nos thérapeutes adaptent chaque rituel à votre séjour — confiez-vous à la conciergerie spa pour un programme sur mesure ou pour réserver avant votre arrivée.",
                es: "Nuestros terapeutas adaptan cada ritual a su estancia — converse con la conserjería spa para un programa a medida o para reservar antes de su llegada.",
              },
              lang
            )}
          </p>
          <Link
            to={ctaHref}
            className="group inline-flex items-center gap-3 bg-[#D4A373] hover:bg-[#FDFBF7] text-[#1A1513] px-7 md:px-8 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors whitespace-nowrap"
            data-testid="spa-cta"
          >
            {ctaLabel}
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default SpaWellnessFeature;
